import { getNewDepartmentsOrder } from 'util/getNewDepartmentsOrder';

import { serializeError } from 'shared/utils/redux';
import { DepartmentModel } from 'api/dto/Department.dto';
import { DepartmentPositionType } from 'model/DepartmentPositionType';
import { selectDepartmentList } from 'reduxStore/department/selectors';
import { selectCurrentProjectOnlyId } from 'reduxStore/project/projectSelector';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { STORE_NAME } from 'reduxStore/department/initialState';
import { saveFile } from 'shared/utils/saveFile';
import { departmentApi } from 'container/departmentApi';

export const fetchDepartmentList = createAsyncThunk(
  `${STORE_NAME}/fetchDepartmentList`,
  ({ projectId }: { projectId: number }) => {
    return departmentApi.list(projectId);
  },
  { serializeError },
);

export const uploadDepartmentList = createAsyncThunk(
  `${STORE_NAME}/uploadDepartmentList`,
  ({ file, projectId }: { file: File; projectId: number }) => {
    return departmentApi.upload(projectId, file);
  },
  { serializeError },
);

export const downloadChangeReport = createAsyncThunk(
  `${STORE_NAME}/downloadChangeReport`,
  ({ file, projectId }: { file: File; projectId: number }) => {
    return departmentApi
      .downloadChangeReport(projectId, file)
      .then((blob) => saveFile(blob, `department-changes-${projectId}`));
  },
  { serializeError },
);

export const uploadDepartmentDry = createAsyncThunk(
  `${STORE_NAME}/uploadDepartmentDry`,
  ({ file, projectId }: { file: File; projectId: number }) => {
    return departmentApi.preview(projectId, file);
  },
  { serializeError },
);

export const addDepartment = createAsyncThunk(
  `${STORE_NAME}/addDepartment`,
  ({ name }: { name: string }, { getState }) => {
    const projectId = selectCurrentProjectOnlyId(getState());
    return departmentApi.add(projectId, name);
  },
  { serializeError },
);

export const editDepartment = createAsyncThunk(
  `${STORE_NAME}/editDepartment`,
  ({ departmentId, name }: { departmentId: number; name: string }) => {
    return departmentApi.edit(departmentId, name);
  },
  { serializeError },
);

export const deleteDepartment = createAsyncThunk(
  `${STORE_NAME}/deleteDepartment`,
  ({ department }: { department: DepartmentModel }) => {
    return departmentApi.delete(department.id);
  },
  { serializeError },
);

export const changeDepartmentPosition = createAsyncThunk(
  `${STORE_NAME}/changeDepartmentPosition`,
  (
    {
      selectedDepartment,
      positionDepartmentId,
      positionType,
    }: {
      selectedDepartment: DepartmentModel;
      positionDepartmentId: number;
      positionType: DepartmentPositionType;
    },
    { getState },
  ) => {
    const departmentList = selectDepartmentList(getState());

    const order = getNewDepartmentsOrder(
      selectedDepartment,
      positionDepartmentId,
      positionType,
      departmentList,
    );

    return departmentApi.reorder(order);
  },
  { serializeError },
);

export const changeDepartmentParent = createAsyncThunk(
  `${STORE_NAME}/changeDepartmentParent`,
  ({ childId, parentId }: { childId: number; parentId: number }) => {
    const newParentId = parentId === 0 ? null : parentId;
    return departmentApi.changeParent(childId, newParentId);
  },
  { serializeError },
);
